/**
 * @file     cardAdapters.ts
 * @readme   ../CLAUDE.md
 * @purpose  Discriminated-union types and pure adapters that normalize curated PropertyListing rows, MLS card rows and owner listings (homes Ray lists for their owners) into a single PropertyCard input shape, plus the visual `Display` mapping used by PropertyCard's render path.
 */

import type { PropertyListing } from '@/core/types/property';
import { propertyRoute } from '@/core/constants/routes';
import { formatStreetLine } from '@/services/mls/addressFormat';
import { isLotPropertyType, isVacantLand } from '@/services/mls/landSpecs';
import { formatOpenHouseLabel } from '@/utils/openHouseTime';
import { compactCompletion } from '@/services/mls/presalesModel';
import type { OwnerListingPublicCard } from '@/core/types/ownerListing';
import {
  mlsRentalFeatures,
  ownerHomeTypeLabel,
  ownerRentalFeatures,
  ownerStreetLine,
  type FairRentBand,
  type RentalFeatures,
} from './rentalCardModel';

/**
 * Card-shaped projection of an MLS row, normalized for PropertyCard.
 *
 * Mirrors `ListingCard` from `@/services/mls/mlsPropertyService` but adds
 * the BCRES attribution fields, which today live on `ListingDetail` only.
 * The MLS service may surface these on cards in PR-D; until then the
 * adapter passes `null` and the card hides the attribution chrome.
 */
import type { MlsListingCardData } from '@/types/listing';
import type { MortgageAssumptions } from '@/services/rent/rentMortgage';
export type { MlsListingCardData };

/** Agent-workspace card actions: when present, the consumer CTAs (Watch /
 *  Compare / like) are replaced by "Add to search" + "Suggest" targeting the
 *  buyer whose profile the card is rendered inside. */
export type AgentCardActions = {
  onAddToSearch: (listing: { listingKey: string; title: string }) => void;
  onSuggest: (listing: { listingKey: string; title: string }) => void;
  /** Buyer footprint labels for a listing (LIKED / VIEWED / SENT / INQUIRED /
   *  SHORTLISTED) painted onto cards in agent mode. */
  getFootprint?: (listingKey: string) => string[];
};

/**
 * What an agent CTA should show for a listing, derived from the buyer's
 * footprint. An action already taken reads as done rather than offering itself
 * again, so an agent can see at a glance what they have already filed.
 *
 * `tone` separates the two disabled states: a shortlisted pick is filed but the
 * buyer cannot see it until the shortlist is sent, so it must not render the
 * same terminal green as 'Sent'. Both stay disabled — re-filing a pick 409s.
 */
export function resolveAgentCtaState(
  footprint: string[],
  action: 'add' | 'suggest',
): { done: boolean; label: string | null; tone: 'done' | 'pending' | 'idle' } {
  if (action === 'suggest') {
    if (footprint.includes('SENT')) return { done: true, label: 'Sent', tone: 'done' };
    if (footprint.includes('SHORTLISTED')) return { done: true, label: 'Not sent yet', tone: 'pending' };
    return { done: false, label: null, tone: 'idle' };
  }
  if (footprint.includes('IN SEARCH')) return { done: true, label: 'In search', tone: 'done' };
  return { done: false, label: null, tone: 'idle' };
}

type CardCommonProps = { locationTag?: string; priority?: boolean; hideLikeButton?: boolean; showMortgage?: boolean; mortgageAssumptions?: MortgageAssumptions; rentComparison?: number; openInSameTab?: boolean; disableLinkNavigation?: boolean; onViewDetails?: () => void; viewDetailsLabel?: string; onInquire?: () => void; agentActions?: AgentCardActions; /** Rentals: where the rent sits against asking rents nearby, when another surface knows it. The chip renders only when set. */ fairRent?: FairRentBand | null };

export type PropertyCardProps =
  | ({ source: 'curated'; listing: PropertyListing } & CardCommonProps)
  | ({ source: 'mls'; listing: MlsListingCardData; /** "Est. rent ~$X/mo": only while the buyer filters on estimated rent. */ showRentEstimate?: boolean } & CardCommonProps)
  /** A home Ray lists for its owner. Never mixed into MLS® lists, maps or compare. */
  | ({ source: 'owner'; listing: OwnerListingPublicCard } & CardCommonProps);

/** The listing sources PropertyCard renders itself; owner listings have their own variant. */
export type MlsOrCuratedCardProps = Exclude<PropertyCardProps, { source: 'owner' }>;

/** Single internal shape rendered by PropertyCard for every variant. */
export type PropertyCardDisplay = {
  href: string;
  title: string;
  subtitle: string;
  price: number | null;
  beds: number | null;
  baths: number | null;
  propertyType: string;
  primaryImageSrc: string | null;
  attribution?: string;
  isMls: boolean;
  /** Card-density enrichment fields. Optional — render when present. */
  sqft?: number | null;
  yearBuilt?: number | null;
  pricePerSqft?: number | null;
  photosCount?: number | null;
  /** "New" (≤3d), "Newly Listed" (≤14d), or null. Derived from listingContractDate. */
  freshnessLabel?: 'New' | 'Newly Listed' | null;
  /** Brokerage name for MLS compliance display on every card. */
  officeName?: string | null;
  /** How far this listing has fallen from its own peak asking price. Set only
   *  on surfaces that looked it up; absent everywhere else. Direction only
   *  without VOW access (no pct, no from). */
  priceCut?: { direction: 'down'; pct?: number; from?: number } | null;
  isRental?: boolean;
  mlsId?: string | null;
  /** Formatted summary of next open house, e.g. "Open Sat 1–3pm" — null when none. */
  nextOpenHouseLabel?: string | null;
  /** When the search included a feature pill that matched this listing only
   *  via public_remarks (e.g. Hot tub on a DDF listing that ships no
   *  SpaFeatures), the card shows a subtle disclosure chip so the buyer
   *  knows it's a soft match. Null when all matches were structured-tag. */
  matchDisclosureLabel?: string | null;
  /** VOW off-market status (feed value) — chip + informational treatment. */
  offMarketStatus?: 'Closed' | 'Pending' | 'Expired' | 'Canceled' | null;
  /** Gated decoy card: blur photo/price/address, click opens the VOW modal. */
  vowGated?: boolean;
  /** Presale pills (brief 08). Each renders only when known; the row keeps
   *  its slot height whenever any presale field is present so grid cards
   *  stay aligned. The year-built metric is dropped on presale rows because
   *  the home is not built. */
  completionEstimate?: string | null;
  gstIncluded?: boolean | null;
  isAssignment?: boolean;
  isPresale?: boolean;
  /** 'lease' on MLS® rentals and owner rental listings: price is monthly rent. */
  listingType?: 'sale' | 'lease';
  /** Where the listing comes from, for analytics and the owner treatment. */
  listingSource?: 'mls' | 'owner' | 'curated';
  /** Rentals only: the available date (YYYY-MM-DD) or null for now, and the feature flags. */
  availableOn?: string | null;
  rentalFeatures?: RentalFeatures;
};

export type PropertyCardViewAction =
  | { kind: 'callback' }
  | { kind: 'link'; href: string; target?: '_blank'; rel?: 'noopener noreferrer' }
  | { kind: 'disabled' };

/** Resolve the View Details behavior independently of the card's surface.
 * Map surfaces supply a callback for their in-page sheet. Every other active
 * listing falls back to its canonical route, respecting same-tab preference. */
export function resolvePropertyCardViewAction({
  href,
  hasViewHandler,
  openInSameTab,
  offMarket,
  vowGated,
  inPageNavigation = false,
}: {
  href: string;
  hasViewHandler: boolean;
  openInSameTab: boolean;
  /** Surface that owns navigation in place (the map panel). Its card wrapper
   *  has its own click handler, so a second navigation from this CTA is always
   *  wrong — the user got the page in a new tab AND the surface reacted. */
  inPageNavigation?: boolean;
  /** Real off-market (VOW) card with a working, auth-gated detail route. */
  offMarket: boolean;
  /** Gated decoy card (no real listing) — click opens the VOW access modal. */
  vowGated: boolean;
}): PropertyCardViewAction {
  // Decoys have no real listing behind them: never link, never open a sheet.
  if (vowGated) return { kind: 'disabled' };
  // Off-market records render in the in-page sheet like any other listing now
  // that it can fetch them (/api/vow/listings/[key]). Only surfaces without a
  // handler fall through to the gated detail route.
  if (offMarket && hasViewHandler && inPageNavigation) return { kind: 'callback' };
  if (offMarket) {
    return openInSameTab || inPageNavigation
      ? { kind: 'link', href }
      : { kind: 'link', href, target: '_blank', rel: 'noopener noreferrer' };
  }
  if (hasViewHandler) return { kind: 'callback' };
  return openInSameTab
    ? { kind: 'link', href }
    : { kind: 'link', href, target: '_blank', rel: 'noopener noreferrer' };
}

function daysBetween(iso: string | null | undefined, ref: Date = new Date()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((ref.getTime() - t) / 86_400_000));
}

export { isLotPropertyType };

/**
 * Compliance-safe freshness label derived from listing date.
 * BCRES policy: "Days on market" must NOT be advertised publicly.
 * Use "New" (≤3 days) / "Newly Listed" (≤14 days) instead.
 */
export function freshnessFromListingDate(
  listingContractDate: string | null | undefined,
  ref: Date = new Date(),
): 'New' | 'Newly Listed' | null {
  const d = daysBetween(listingContractDate, ref);
  if (d == null) return null;
  if (d <= 3) return 'New';
  if (d <= 14) return 'Newly Listed';
  return null;
}

export function curatedToCardProps(
  listing: PropertyListing,
  locationTag?: string,
): PropertyCardProps {
  return { source: 'curated', listing, locationTag };
}

export function mlsToCardProps(
  card: MlsListingCardData,
  locationTag?: string,
): PropertyCardProps {
  return { source: 'mls', listing: card, locationTag };
}

// Street-address formatting now lives in the canonical @/services/mls/addressFormat
// module (single source of truth for the unit-designator "#" rule). Re-exported
// here so existing importers of cardAdapters keep working.
export { formatStreetLine };

/**
 * BCRES attribution line for MLS cards. Returns `undefined` when both
 * agent and office are missing so the chrome can be hidden entirely.
 *
 * Format: "{agent}, {office}" with graceful fallback when only one of
 * agent/office is known.
 */
export function formatBcresAttribution(
  agentFullName: string | null | undefined,
  officeName: string | null | undefined,
  _modificationTimestamp: string,
): string | undefined {
  const agent = agentFullName?.trim() || null;
  const office = officeName?.trim() || null;
  if (!agent && !office) return undefined;

  return agent && office ? `${agent}, ${office}` : (agent ?? office)!;
}

/** Feature key → user-facing label for the remarks-only disclosure chip.
 *  Mirrors filterPills.ts pill labels; kept in sync manually so this file
 *  doesn't take a dependency on the server-side pill registry. */
const FEATURE_LABELS: Record<string, string> = {
  garage: 'Garage',
  air_conditioning: 'Air conditioning',
  fireplace: 'Fireplace',
  pool: 'Pool',
  hot_tub: 'Hot tub',
  waterfront: 'Waterfront',
  view: 'View',
  suite: 'Suite',
  open_concept: 'Open concept',
  multi_gen: 'Multi-gen',
};

/** Build the card disclosure label from per-pill evidence. Shows only the
 *  pills whose match came from public_remarks (no MLS-tagged column). */
function buildMatchDisclosureLabel(
  evidence: Partial<Record<string, 'structured' | 'remarks'>> | undefined,
): string | null {
  if (!evidence) return null;
  const remarksOnly = Object.entries(evidence)
    .filter(([, tier]) => tier === 'remarks')
    .map(([key]) => FEATURE_LABELS[key] ?? key);
  if (remarksOnly.length === 0) return null;
  if (remarksOnly.length === 1) return `${remarksOnly[0]} mentioned in remarks`;
  return `${remarksOnly.slice(0, -1).join(', ')} & ${remarksOnly.at(-1)} mentioned in remarks`;
}

/**
 * Pure function mapping `PropertyCardProps` to its visual `Display` projection.
 * Extracted from PropertyCard so the union mapping is unit-testable without
 * a DOM renderer.
 */
export type ToDisplayOptions = {
  /**
   * Reference instant for the time-relative chips (freshness, next open house).
   * Hydrated card surfaces pass `useHydratedNow()`: `null` before mount so the
   * server HTML and the first client render agree (no chip), the real clock
   * after. Omit on client-only surfaces to use the current time.
   */
  now?: Date | null;
};

export function toDisplay(props: PropertyCardProps, options: ToDisplayOptions = {}): PropertyCardDisplay {
  const now = options.now === undefined ? new Date() : options.now;
  if (props.source === 'owner') {
    const o = props.listing;
    return {
      href: o.path,
      title: ownerStreetLine(o.address),
      subtitle: o.address.city,
      price: o.price,
      beds: o.bedrooms,
      baths: o.bathrooms,
      propertyType: ownerHomeTypeLabel(o.homeType) ?? '',
      primaryImageSrc: o.coverUrl,
      attribution: undefined,
      isMls: false,
      isRental: o.kind === 'lease',
      sqft: o.livingArea,
      photosCount: o.photoCount,
      listingType: o.kind === 'lease' ? 'lease' : 'sale',
      listingSource: 'owner',
      availableOn: o.availableOn,
      rentalFeatures: ownerRentalFeatures(o),
    };
  }

  if (props.source === 'curated') {
    const l = props.listing;
    return {
      href: propertyRoute(l.slug),
      title: l.address,
      subtitle: l.neighbourhood,
      price: l.price,
      beds: l.bedrooms,
      baths: l.bathrooms,
      propertyType: l.type,
      primaryImageSrc: l.images[0] ?? null,
      attribution: undefined,
      isMls: false,
      isRental: l.type?.toLowerCase().includes('lease') || l.type?.toLowerCase().includes('rental'),
      sqft: l.sqft ?? null,
      pricePerSqft: l.pricePerSqft ?? null,
      photosCount: l.images?.length ?? null,
      listingSource: 'curated',
    };
  }

  const m = props.listing;
  const street = formatStreetLine({
    streetNumber: m.streetNumber,
    streetName: m.streetName,
    streetSuffix: m.streetSuffix,
    unitNumber: m.unitNumber ?? null,
  });
  const title = street || m.city || 'Address withheld';
  // Always use the proxy for card images so next/image can compress them
  // from ~700KB JPEG to ~90KB WebP. Direct CDN URLs bypass compression.
  const primaryImageSrc = m.primaryMediaKey
    ? `/api/mls/media/${encodeURIComponent(m.primaryMediaKey)}`
    : (m.primaryMediaUrl ?? null);

  const lot = isVacantLand(m.propertyType, m.propertySubType);
  // DDF ships a lot's area in LivingArea; it is never interior space.
  const sqft = lot ? null : (m.livingArea ?? null);
  const pricePerSqft =
    m.listPrice != null && sqft && sqft > 0 ? Math.round(m.listPrice / sqft) : null;

  const offMarket = m.offMarketStatus ?? null;
  const gatedDecoy = m.vowGated === true;
  const lease = m.listingType === 'lease';
  return {
    // Gated decoys stay inert (click opens the VOW modal). Real off-market
    // listings link to their auth-gated detail route; active listings to the
    // public route.
    href: gatedDecoy
      ? '#'
      : offMarket
        ? `/properties/off-market/${m.listingKey}`
        : `/properties/${m.listingSlug ?? m.listingKey}`,
    title,
    subtitle: m.city ?? '',
    price: offMarket === 'Closed' ? (m.closePrice ?? m.listPrice) : m.listPrice,
    // Land/lot cards have no dwelling specs. Zero beds/baths/sqft is feed
    // filler, not a studio, and makes the card look broken next to houses.
    beds: lot ? null : m.bedroomsTotal,
    baths: lot ? null : m.bathroomsTotalInteger,
    propertyType: m.propertyType,
    primaryImageSrc,
    attribution: formatBcresAttribution(m.agentFullName, m.officeName, m.modificationTimestamp),
    isMls: true,
    sqft,
    yearBuilt: m.yearBuilt ?? null,
    // Rent per square foot is not a figure renters compare by.
    pricePerSqft: lease ? null : pricePerSqft,
    photosCount: m.photosCount ?? null,
    freshnessLabel: offMarket || !now ? null : freshnessFromListingDate(m.listingContractDate ?? null, now),
    officeName: m.officeName ?? null,
    priceCut: m.priceCut ?? null,
    isRental: lease || m.propertyType?.toLowerCase().includes('lease'),
    listingType: lease ? 'lease' : 'sale',
    listingSource: 'mls',
    mlsId: m.listingId ?? null,
    nextOpenHouseLabel: offMarket || !now ? null : formatOpenHouseLabel(m.nextOpenHouse ?? null, now),
    matchDisclosureLabel: buildMatchDisclosureLabel(m.pillEvidence),
    offMarketStatus: offMarket,
    vowGated: m.vowGated === true,
    ...presaleDisplay(m),
    ...(lease
      ? {
          availableOn: m.availabilityDate ?? null,
          rentalFeatures: mlsRentalFeatures(m),
          // No price-cut, open-house or presale language on a rental.
          priceCut: null,
          nextOpenHouseLabel: null,
        }
      : {}),
  };
}

function presaleDisplay(m: MlsListingCardData): Pick<PropertyCardDisplay, 'isPresale' | 'completionEstimate' | 'gstIncluded' | 'isAssignment'> {
  const isPresale = m.completionEstimate !== undefined || m.gstIncluded !== undefined || m.isAssignment !== undefined;
  if (!isPresale) return {};
  return {
    isPresale: true,
    completionEstimate: m.completionEstimate ? compactCompletion(m.completionEstimate) : null,
    gstIncluded: m.gstIncluded ?? null,
    isAssignment: m.isAssignment === true,
  };
}
